import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { createUseStyles } from 'react-jss';
import Button from './Button';
import { S, A, TONES, DARK } from './tokens';

type Props = {
  /** A question or statement naming the thing, e.g. "Request deletion of “Home”?" */
  title: string;
  /** What happens, and what doesn't. */
  children?: React.ReactNode;
  /** The action, named exactly: "Request deletion", not "OK". */
  confirmLabel: string;
  cancelLabel?: string;
  /** Destructive actions get a red button, and Cancel has the focus. */
  danger?: boolean;
  /** May return a promise: the dialog waits, and stays open with the error if it fails. */
  onConfirm: () => unknown;
  onClose: () => void;
  /** Disables the confirm button until the content is ready (e.g. a reason is typed). */
  confirmDisabled?: boolean;
  testId?: string;
  width?: number;
};

const useStyles = createUseStyles({
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 3000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    background: 'rgba(23, 26, 32, 0.36)',
    animation: '$gxFade 120ms ease-out',
    [DARK]: { background: S.scrim }
  },
  '@keyframes gxFade': { from: { opacity: 0 }, to: { opacity: 1 } },
  '@keyframes gxRise': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
  dialog: {
    boxSizing: 'border-box',
    width: '100%',
    maxHeight: 'calc(100vh - 32px)',
    overflowY: 'auto',
    padding: '22px 24px 20px',
    background: A.surface,
    borderRadius: 12,
    boxShadow: A.pop,
    fontFamily: S.ui,
    color: S.fg,
    animation: '$gxRise 160ms ease-out',
    outline: 'none'
  },
  title: { margin: 0, fontSize: 18, fontWeight: 600, lineHeight: 1.3 },
  body: { margin: '10px 0 0', fontSize: 14, lineHeight: 1.55, color: '#3A3D44', '& p': { margin: '0 0 8px' }, [DARK]: { color: S.fg } },
  error: { margin: '14px 0 0', padding: '8px 10px', borderRadius: 6, background: 'rgba(200, 55, 55, 0.08)', color: TONES.red.fg, fontSize: 13, [DARK]: { background: TONES.red.bg } },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 22, flexWrap: 'wrap' }
});

/**
 * Asks before an action that's hard to undo. The buttons say what will
 * happen, so nobody has to read the question to answer it.
 */
const ConfirmDialog: React.FC<Props> = ({
  title, children, confirmLabel, cancelLabel = 'Cancel', danger, onConfirm, onClose, confirmDisabled, testId, width = 440
}) => {
  const c = useStyles();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLElement>(null);
  const confirm = useRef<HTMLElement>(null);
  const returnTo = useRef<Element | null>(typeof document !== 'undefined' ? document.activeElement : null);
  const mounted = useRef(true);
  const titleId = useRef(`gx-dialog-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    mounted.current = true;
    // Safe default: Cancel for destructive actions, the action otherwise. A text field inside takes precedence.
    const field = dialog.current?.querySelector<HTMLElement>('textarea, input, select');
    (field || (danger ? cancel.current : confirm.current))?.focus();
    return () => { mounted.current = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Back to where focus was, in the same step that removes the dialog (a passive effect would
  // leave focus on the page body for a moment).
  useLayoutEffect(() => {
    const back = returnTo.current as HTMLElement | null;
    return () => { if (back && typeof back.focus === 'function' && document.contains(back)) back.focus(); };
  }, []);

  const close = () => { if (!busy) onClose(); };

  const run = async () => {
    if (busy || confirmDisabled) return;
    setError(null);
    setBusy(true);
    try {
      await onConfirm();
      if (mounted.current) { setBusy(false); onClose(); }
    } catch (e) {
      if (mounted.current) {
        setBusy(false);
        setError((e as Error)?.message || 'That didn’t work. Try again.');
      }
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); return; }
    if (e.key !== 'Tab' || !dialog.current) return;
    // Keep Tab inside the dialog.
    const items = Array.from(dialog.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return ReactDOM.createPortal(
    <div className={c.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }} onClick={e => e.stopPropagation()}>
      <div
        ref={dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={c.dialog}
        style={{ maxWidth: width }}
        onKeyDown={onKeyDown}
        data-testid={testId || 'confirm-dialog'}
        tabIndex={-1}
      >
        <h2 id={titleId} className={c.title}>{title}</h2>
        {children && <div className={c.body}>{children}</div>}
        {error && <div className={c.error} role="alert">{error}</div>}
        <div className={c.actions}>
          <Button ref={cancel} variant="secondary" onClick={close} disabled={busy} data-testid="confirm-cancel">{cancelLabel}</Button>
          <Button ref={confirm} variant={danger ? 'danger' : 'primary'} onClick={run} busy={busy} disabled={confirmDisabled} data-testid="confirm-ok">
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
